<template>
  <v-main>
    <loader :loading="loading"></loader>
    <v-container fluid class="pa-6">
      <div class="flex flex-col space-y-6">
        <!-- Hero Header Card with Step Progress -->
        <v-card v-if="!this.mapper" class="req-hero-card mb-6" elevation="6">
          <div class="req-hero-header">
            <div class="d-flex align-center">
              <v-avatar size="48" class="req-hero-logo mr-4">
                <v-icon dark size="26">mdi-clipboard-check-multiple-outline</v-icon>
              </v-avatar>
              <div>
                <h1 class="req-hero-title">{{ $tc('requirements.title', 2) }}</h1>
                <p class="req-hero-subtitle mb-0">
                  Review and select which requirements will be included in your certification
                </p>
              </div>
            </div>
          </div>

          <!-- Step Progress -->
          <div class="req-steps-bar">
            <div class="req-step req-step-done">
              <div class="req-step-circle req-step-circle-done">
                <v-icon size="18" color="white">mdi-check</v-icon>
              </div>
              <span class="req-step-label">Define a Subject of Certification</span>
            </div>
            <div class="req-step-line req-step-line-done"></div>
            <div class="req-step req-step-done">
              <div class="req-step-circle req-step-circle-done">
                <v-icon size="18" color="white">mdi-check</v-icon>
              </div>
              <span class="req-step-label">Configure</span>
            </div>
            <div class="req-step-line req-step-line-done"></div>
            <div class="req-step req-step-active">
              <div class="req-step-circle req-step-circle-active">3</div>
              <span class="req-step-label">Review</span>
            </div>
          </div>
        </v-card>

        <!-- Requirements Selection Guide -->
        <v-card v-if="!this.mapper" class="req-guide-card mb-4" elevation="2">
          <div class="req-guide-header">
            <div class="req-guide-header-icon">
              <v-icon color="white" size="20">mdi-information-outline</v-icon>
            </div>
            <h3 class="req-guide-header-title">Requirements Selection Guide</h3>
          </div>
          <v-card-text class="pa-5">
            <p class="text-body-1 mb-4" style="color: #546e7a;">
              Decide which requirements will be included in your Subject of Certification. Review
              all requirements carefully before proceeding.
            </p>

            <v-row>
              <v-col cols="12" md="6">
                <div class="req-info-box">
                  <div class="req-info-box-header">
                    <div class="req-info-icon req-info-icon--blue">
                      <v-icon size="18" color="white">mdi-clipboard-list</v-icon>
                    </div>
                    <span class="req-info-title">Requirements Management</span>
                  </div>
                  <ul class="req-feature-list">
                    <li>By default, all requirements are selected and in scope</li>
                    <li>The Threat Model column indicates selection from Threat Spider</li>
                    <li>Uncheck the box to set a requirement as out of scope</li>
                    <li>Use search to find specific requirements</li>
                    <li>Click the info icon to preview requirement details</li>
                  </ul>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="req-info-box">
                  <div class="req-info-box-header">
                    <div class="req-info-icon req-info-icon--amber">
                      <v-icon size="18" color="white">mdi-alert-circle-outline</v-icon>
                    </div>
                    <span class="req-info-title">Out of Scope Requirements</span>
                  </div>
                  <ul class="req-feature-list">
                    <li>Unchecking a requirement requires a justification</li>
                    <li>Justifications explain why the requirement is not applicable</li>
                    <li>Click "Proceed" when finished to create your Subject of Certification</li>
                  </ul>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Data Table Section -->
        <v-card class="req-table-card" elevation="2">
          <div class="req-table-header">
            <div class="d-flex align-center" style="gap: 10px;">
              <div class="req-table-header-icon">
                <v-icon color="white" size="18">mdi-table</v-icon>
              </div>
              <span class="req-table-header-title">{{ $tc('requirements.title', 2) }}</span>
            </div>
          </div>
          <div class="pa-5">
            <div class="d-flex align-center mb-4" style="gap: 12px;">
              <v-select
                v-model="searchField"
                :items="searchFields"
                item-text="text"
                item-value="value"
                :label="$t('search-field')"
                hide-details
                outlined
                dense
                style="max-width: 200px;"
              ></v-select>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                :label="$t('search')"
                hide-details
                class="flex-grow-1"
                outlined
                dense
                @keyup.enter="applySearch"
              />
            </div>

            <v-data-table
              :headers="getHeaders"
              :items="filteredComponents"
              :search="search"
              :custom-filter="customFilter"
              class="req-data-table elevation-0"
              :loading="loading"
              :items-per-page="5"
              :footer-props="{
                showFirstLastPage: true,
                firstIcon: 'mdi-arrow-collapse-left',
                lastIcon: 'mdi-arrow-collapse-right',
                prevIcon: 'mdi-arrow-left',
                nextIcon: 'mdi-arrow-right',
                disableItemsPerPage: false,
                itemsPerPageText: getItemsPerPageText,
                showCurrentPage: true,
              }"
              must-sort
              :sort-desc="true"
            >
              <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex align-center justify-center gap-1">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-on="on" v-bind="attrs" small @click="openReviewModal(item)">
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <span>{{ $t('Review') }}</span>
                  </v-tooltip>
                </div>
              </template>
              <template v-slot:[`item.description`]="{ item }">
                <span class="truncate-text">{{ truncateText(item.description, 40) }}</span>
              </template>
              <template v-slot:[`item.checkbox`]="{ item }">
                <div class="d-flex justify-center">
                  <v-checkbox
                    v-model="item.checked"
                    @change="handleCheckboxChange(item)"
                    color="primary"
                  />
                </div>
              </template>
              <template v-slot:[`item.origin`]="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <a v-if="item.origin === 'spider'" href="http://localhost:5000" target="_blank">
                      <v-icon v-bind="attrs" v-on="on" class="cursor-pointer">
                        mdi-spider
                      </v-icon>
                    </a>
                    <span v-else v-bind="attrs" v-on="on">
                      N/A
                    </span>
                  </template>
                  <span>{{
                    item.origin === 'spider'
                      ? 'Click to view in Threat Model: Threat Spider'
                      : 'Not Applicable'
                  }}</span>
                </v-tooltip>
              </template>
            </v-data-table>

            <!-- Action Buttons -->
            <div class="d-flex justify-space-between mt-5">
              <v-btn class="req-btn-back" @click="$router.go(-1)">
                <v-icon left small>mdi-arrow-left</v-icon>
                Back
              </v-btn>
              <v-btn class="req-btn-proceed" @click="submitSOC">
                <v-icon left small>mdi-check-circle-outline</v-icon>
                Proceed
              </v-btn>
            </div>
          </div>
        </v-card>

        <v-dialog v-model="previewSocClicked" max-width="700">
          <v-card v-if="socInfo" class="requirement-preview-card">
            <v-card-actions class="dialog-header">
              <v-spacer></v-spacer>
              <v-btn icon @click="previewSocClicked = false" color="white">
                <v-icon>mdi-close-circle</v-icon>
              </v-btn>
            </v-card-actions>

            <v-card-title class="headline-section">
              <div class="d-flex flex-column py-4">
                <span class="text-h5 font-weight-bold white--text mb-1">{{ socInfo.name }}</span>
                <span class="text-subtitle-1 white--text">ID: {{ socInfo.id }}</span>
              </div>
            </v-card-title>

            <v-card-text class="mt-4">
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <v-card outlined class="pa-4 mb-3">
                      <div class="d-flex align-center mb-2">
                        <v-icon color="primary" class="mr-2">mdi-format-list-text</v-icon>
                        <div class="text-subtitle-1 font-weight-bold">
                          {{ $t('soc.headers.type') }}
                        </div>
                      </div>
                      <div class="text-body-1">{{ socInfo.type }}</div>
                    </v-card>
                  </v-col>

                  <v-col cols="12">
                    <v-card outlined class="pa-4 mb-3">
                      <div class="d-flex align-center mb-2">
                        <v-icon color="primary" class="mr-2">mdi-text-box</v-icon>
                        <div class="text-subtitle-1 font-weight-bold">
                          {{ $t('soc.headers.description') }}
                        </div>
                      </div>
                      <div class="text-body-1">{{ socInfo.description }}</div>
                    </v-card>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-card outlined class="pa-4 mb-3">
                      <div class="d-flex align-center mb-2">
                        <v-icon color="primary" class="mr-2">mdi-target</v-icon>
                        <div class="text-subtitle-1 font-weight-bold">
                          {{ $t('soc.headers.scope') }}
                        </div>
                      </div>
                      <div class="text-body-1">{{ socInfo.standardFamily }}</div>
                    </v-card>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-card outlined class="pa-4 mb-3">
                      <div class="d-flex align-center mb-2">
                        <v-icon color="primary" class="mr-2">mdi-calendar</v-icon>
                        <div class="text-subtitle-1 font-weight-bold">
                          {{ $t('soc.headers.date') }}
                        </div>
                      </div>
                      <div class="text-body-1">{{ socInfo.creationDate }}</div>
                    </v-card>
                  </v-col>

                  <v-col cols="12">
                    <v-card outlined class="pa-4">
                      <div class="d-flex align-center mb-2">
                        <v-icon color="primary" class="mr-2">mdi-tag</v-icon>
                        <div class="text-subtitle-1 font-weight-bold">
                          {{ $t('soc.headers.version') }}
                        </div>
                      </div>
                      <div class="text-body-1">{{ socInfo.versionLabel }}</div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Review Modal -->
        <v-dialog v-model="reviewModalOpen" max-width="900" scrollable>
          <v-card class="req-dialog-card" v-if="selectedRequirement">
            <div class="req-dialog-header">
              <div class="d-flex align-center">
                <div class="req-dialog-header-icon mr-3">
                  <v-icon color="white" size="20">mdi-file-document-outline</v-icon>
                </div>
                <span class="text-h6 font-weight-bold white--text">Requirement Review</span>
              </div>
              <v-btn icon @click="reviewModalOpen = false" color="white">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>

            <v-card-text class="pa-6">
              <v-row>
                <v-col cols="12" md="6">
                  <div class="req-review-field">
                    <div class="req-review-field-label">
                      <v-icon small color="#1976d2" class="mr-1">mdi-identifier</v-icon>
                      ID
                    </div>
                    <div class="req-review-field-value">{{ selectedRequirement.id }}</div>
                  </div>
                </v-col>

                <v-col cols="12" md="6">
                  <div class="req-review-field">
                    <div class="req-review-field-label">
                      <v-icon small color="#7b1fa2" class="mr-1">mdi-tag</v-icon>
                      Req ID
                    </div>
                    <div class="req-review-field-value">{{ selectedRequirement.reqId }}</div>
                  </div>
                </v-col>

                <v-col cols="12">
                  <div class="req-review-field">
                    <div class="req-review-field-label">
                      <v-icon small color="#00695c" class="mr-1">mdi-format-title</v-icon>
                      Title
                    </div>
                    <div class="req-review-field-value">{{ selectedRequirement.title }}</div>
                  </div>
                </v-col>

                <v-col cols="12" md="6">
                  <div class="req-review-field">
                    <div class="req-review-field-label">
                      <v-icon small color="#e65100" class="mr-1">mdi-folder-open</v-icon>
                      Family
                    </div>
                    <div class="req-review-field-value">{{ selectedRequirement.family }}</div>
                  </div>
                </v-col>

                <v-col cols="12" md="6">
                  <div class="req-review-field">
                    <div class="req-review-field-label">
                      <v-icon small color="#1565c0" class="mr-1">mdi-origin</v-icon>
                      Origin
                    </div>
                    <div class="req-review-field-value">
                      {{ selectedRequirement.origin === 'spider' ? 'Threat Spider' : 'Database' }}
                    </div>
                  </div>
                </v-col>

                <v-col cols="12">
                  <div class="req-review-field">
                    <div class="req-review-field-label">
                      <v-icon small color="#546e7a" class="mr-1">mdi-text-box</v-icon>
                      Description
                    </div>
                    <div class="req-review-field-value" style="line-height: 1.7;">
                      {{ selectedRequirement.description || 'No description available' }}
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-actions class="pa-4" style="border-top: 1px solid #e0e0e0;">
              <v-spacer></v-spacer>
              <v-btn class="req-btn-proceed" @click="reviewModalOpen = false">
                <v-icon left small>mdi-check</v-icon>
                Close
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Justification Dialog -->
        <v-dialog
          v-model="justificationDialog"
          max-width="520px"
          @click:outside="cancelJustification"
          @input="val => !val && cancelJustification()"
        >
          <v-card class="req-dialog-card">
            <div class="req-dialog-header req-dialog-header--amber">
              <div class="d-flex align-center">
                <div class="req-dialog-header-icon req-dialog-header-icon--amber mr-3">
                  <v-icon color="white" size="20">mdi-alert-circle-outline</v-icon>
                </div>
                <span class="text-h6 font-weight-bold white--text">Out of Scope Justification</span>
              </div>
            </div>

            <v-card-text class="pa-5 pt-6">
              <p class="text-body-1 mb-4" style="color: #546e7a;">
                Please provide a justification for excluding this requirement from the scope:
              </p>
              <v-textarea
                v-model="justificationText"
                outlined
                label="Justification"
                :rules="[v => !!v || 'Justification is required']"
                required
                rows="4"
                class="req-justification-input"
              ></v-textarea>
            </v-card-text>

            <v-card-actions class="pa-4" style="border-top: 1px solid #e0e0e0;">
              <v-spacer></v-spacer>
              <v-btn class="req-btn-back mr-2" @click="cancelJustification">
                Cancel
              </v-btn>
              <v-btn class="req-btn-proceed" @click="saveJustification">
                <v-icon left small>mdi-content-save</v-icon>
                Save
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </v-container>
  </v-main>
</template>

<script>
import util from '@/mixins/util.js';
// import Header from '@/components/helpers/Header';
import Loader from '@/components/helpers/Loader.vue';
import EventBus from '@/helpers/event-bus.js';
import Soc from '@/modules/soc/api/soc.js';
import IecRequirements from '@/modules/requirements/api/iecRequirements.js';

export default {
  name: 'ComponentList',
  components: {
    // Header,
    Loader,
  },
  mixins: [util],
  props: {
    mapper: {
      type: Boolean,
      required: false,
    },
    subject: {
      type: String,
      required: false,
      default: '',
    },
  },
  computed: {
    getItemsPerPageText() {
      const t = this.$t.bind(this);
      return t('requirements.table-footer');
    },
    filteredComponents() {
      const threatSpiderStandard = this.$route.query.threatSpiderStandard;

      let spiderRequirements = [];
      if (threatSpiderStandard) {
        try {
          const parsedStandard =
            typeof threatSpiderStandard === 'string'
              ? JSON.parse(threatSpiderStandard)
              : threatSpiderStandard;

          if (parsedStandard && parsedStandard.Requirements) {
            spiderRequirements = parsedStandard.Requirements.map(req => ({
              id: req.RequirementID || '',
              reqId: req.RequirementID || '',
              title: req.RequirementName || '',
              family: parsedStandard.StandardName || 'Unknown Standard',
              description: req.RequirementDescription || '',
              checked: true,
              origin: 'spider',
            }));
          }
        } catch (error) {
          console.error('Parse error:', error);
          console.error('Raw data:', threatSpiderStandard);
        }
      }

      let localRequirements = this.components;

      const allRequirementsMap = {};
      spiderRequirements.forEach(req => {
        allRequirementsMap[req.id] = req;
      });
      localRequirements.forEach(req => {
        if (!allRequirementsMap[req.id]) {
          allRequirementsMap[req.id] = req;
        }
      });

      return Object.values(allRequirementsMap);
    },
    getHeaders() {
      const t = this.$t.bind(this);
      return [
        {
          text: t('requirements.headers.id'),
          value: 'id',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '5%',
        },
        {
          text: 'Req ID',
          value: 'reqId',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '8%',
        },
        {
          text: t('requirements.headers.title'),
          value: 'title',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '15%',
        },
        {
          text: t('requirements.headers.family'),
          value: 'family',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '12%',
        },
        {
          text: t('requirements.headers.description'),
          value: 'description',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '12%',
        },
        {
          text: this.$t('Threat Model'),
          value: 'origin',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: t('requirements.headers.actions'),
          value: 'actions',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '8%',
        },
        {
          text: t('requirements.headers.checkbox'),
          value: 'checkbox',
          align: 'center',
          class: 'text-uppercase text-subtitle-2 font-weight-black',
          width: '8%',
        },
      ];
    },
    searchFields() {
      return [
        { text: this.$t('search-field-all'), value: 'all' },
        { text: this.$t('requirements.headers.id'), value: 'id' },
        { text: 'Req ID', value: 'reqId' },
        { text: this.$t('requirements.headers.title'), value: 'title' },
        { text: this.$t('requirements.headers.family'), value: 'family' },
        { text: this.$t('requirements.headers.description'), value: 'description' },
      ];
    },
  },
  data() {
    return {
      loading: false,
      components: [],
      search: null,
      searchField: 'all',
      deleteClicked: false,
      deleteItem: null,
      finalizeClicked: false,
      finalizeItem: null,
      studyInfo: null,
      previewSocClicked: false,
      selectedComponentId: null,
      socInfo: null,
      justificationDialog: false,
      currentItem: null,
      justificationText: '',
      reviewModalOpen: false,
      selectedRequirement: null,
      certificationName: this.$route.query.name || '',
      certificationDescription: this.$route.query.description || '',
      family: this.$route.query.family || '',
      secureDevelopment: this.$route.query.secureDevelopment || false,
      configManagement: this.$route.query.configManagement || false,
      incidentResponse: this.$route.query.incidentResponse || false,
      componentType: this.$route.query.componentType || '',
      osFirmwareInfo: this.$route.query.osFirmwareInfo || '',
      communicationInterfaces: this.$route.query.communicationInterfaces || '',
      protocols: this.$route.query.protocols || '',
      securityFeatures: this.$route.query.securityFeatures || '',
      serviceProviderType: this.$route.query.serviceProviderType || '',
      activitiesOrSlas: this.$route.query.activitiesOrSlas || '',
      origin: this.$route.query.origin || 'manual',
    };
  },
  created() {
    this.$emit('loading', true);
    this.loading = true;
    this.fetchComponents();
    this.$emit('loading', false);
    this.loading = false;
  },
  methods: {
    truncateText(text, maxLength = 50) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },
    async fetchComponents() {
      try {
        const family = this.$route.query.family;
        if (family) {
          const response = await IecRequirements.getByFamily(family);
          console.log(family);
          console.log(response.data);
          const iecRequirements = response.data || response;
          console.log(iecRequirements);
          this.components = iecRequirements.map(req => ({
            id: req.id,
            reqId: req.reqId,
            title: req.title || 'N/A',
            family: req.part,
            mappedToSoc: '',
            description: req.description || 'N/A',
            checked: true,
            justification: '',
            origin: 'database',
          }));
        }
      } catch (error) {
        console.error('Error fetching IEC requirements:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'red',
        });
      } finally {
        this.$emit('loading', false);
        this.loading = false;
      }
    },
    async submitSOC() {
      const payload = {
        name: this.certificationName,
        description: this.certificationDescription,
        standardFamily: this.$route.query.family,
        notes: 'Some important notes',
        versionLabel: 'v1.0',
        origin: this.origin,
        socComponentDto: {
          socVersionId: 1,
          componentType: this.componentType || 'Default Component Type',
          name: 'SOC Component',
          securityLevel: 'High',
          osFirmware: this.osFirmwareInfo || 'Default OS',
          protocols: this.protocols || 'Default Protocols',
          deploymentContext: 'Cloud',
          securityFeatures: this.securityFeatures || 'Default Features',
          description: 'A security component',
        },
        socOrganizationalUnitDto: {
          name: 'Cybersecurity Department',
          description: 'Handles security',
          socVersionId: 1,
        },
        socServiceProviderDto: {
          serviceProviderType: this.serviceProviderType || '',
          activitiesOrSlas: this.activitiesOrSlas || '',
        },
        socProcessDto: {
          secureDevelopment: this.secureDevelopment,
          configManagement: this.configManagement,
          incidentResponse: this.incidentResponse,
        },
        socRequirementLinksDto: this.filteredComponents.map(component => ({
          title: component.title,
          socVersionId: 1,
          requirementId: component.reqId,
          inScope: component.checked,
          origin: this.origin,
          justification: component.checked ? 'Essential for compliance' : component.justification,
          description: component.description,
        })),
      };

      try {
        await Soc.create(payload);
        EventBus.$emit('snackbar', {
          text: this.$t('Subject of Certification Created Successfully'),
          color: 'green',
        });
        this.$router.push({ name: 'SoCList' });
      } catch (error) {
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'red',
        });
      }
    },
    enablePreviewAssetDialog(item) {
      this.socInfo = {
        id: item.id,
        name: item.title,
        type: item.family,
        description: item.description,
        standardFamily: item.family,
        creationDate: new Date().toLocaleDateString(),
        versionLabel: 'v1.0',
      };
      this.previewSocClicked = true;
    },
    handleCheckboxChange(item) {
      if (!item.checked) {
        this.currentItem = item;
        this.justificationDialog = true;
      }
    },
    cancelJustification() {
      if (this.currentItem) {
        this.currentItem.checked = true;
      }
      this.justificationDialog = false;
      this.justificationText = '';
      this.currentItem = null;
    },
    saveJustification() {
      if (!this.justificationText) {
        return;
      }

      if (this.currentItem) {
        this.currentItem.justification = this.justificationText;
      }

      EventBus.$emit('snackbar', {
        text: 'Justification saved successfully',
        color: 'success',
      });

      this.justificationDialog = false;
      this.justificationText = '';
      this.currentItem = null;
    },
    customFilter(value, search, item) {
      if (!search) return true;

      search = search.toString().toLowerCase();

      if (this.searchField === 'all') {
        return Object.keys(item).some(key => {
          if (key === 'actions' || key === 'raw' || item[key] === null || item[key] === undefined) {
            return false;
          }

          try {
            const itemValue = item[key].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        });
      } else {
        if (item[this.searchField] !== null && item[this.searchField] !== undefined) {
          try {
            const itemValue = item[this.searchField].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        }
        return false;
      }
    },
    applySearch() {
      // Placeholder for additional search logic
    },
    openReviewModal(item) {
      this.selectedRequirement = item;
      this.reviewModalOpen = true;
    },
  },
};
</script>

<style scoped>
/* ─── Hero Card ─── */
.req-hero-card {
  border-radius: 16px !important;
  overflow: hidden;
}
.req-hero-header {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%);
  padding: 28px 32px 20px;
  color: #fff;
}
.req-hero-logo {
  background: rgba(255, 255, 255, 0.18);
  border: 2px solid rgba(255, 255, 255, 0.3);
}
.req-hero-title {
  font-size: 1.55rem;
  font-weight: 700;
  color: #fff;
  margin: 0;
  letter-spacing: 0.3px;
}
.req-hero-subtitle {
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.92rem;
  margin-top: 2px;
}

/* ─── Step Progress Bar ─── */
.req-steps-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px 32px;
  background: #fff;
  gap: 0;
}
.req-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
}
.req-step-circle {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.95rem;
  margin-bottom: 6px;
  transition: all 0.3s;
}
.req-step-circle-done {
  background: linear-gradient(135deg, #388e3c 0%, #66bb6a 100%);
  color: #fff;
  box-shadow: 0 2px 8px rgba(56, 142, 60, 0.25);
}
.req-step-circle-active {
  background: linear-gradient(135deg, #00405e 0%, #0097a7 100%);
  color: #fff;
  box-shadow: 0 2px 12px rgba(0, 151, 167, 0.35);
  animation: req-pulse 2s infinite;
}
.req-step-circle-pending {
  background: #e0e0e0;
  color: #9e9e9e;
}
@keyframes req-pulse {
  0%,
  100% {
    box-shadow: 0 2px 12px rgba(0, 151, 167, 0.35);
  }
  50% {
    box-shadow: 0 2px 20px rgba(0, 151, 167, 0.55);
  }
}
.req-step-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #546e7a;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.req-step-line {
  flex: 1;
  height: 3px;
  min-width: 60px;
  border-radius: 2px;
  margin: 0 6px;
  margin-bottom: 22px;
}
.req-step-line-done {
  background: linear-gradient(90deg, #388e3c, #66bb6a);
}
.req-step-line-pending {
  background: #e0e0e0;
}

/* ─── Guide Card ─── */
.req-guide-card {
  border-radius: 16px !important;
  overflow: hidden;
}
.req-guide-header {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.req-guide-header-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}
.req-guide-header-title {
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
}

/* ─── Info Boxes ─── */
.req-info-box {
  background: #f8fafc;
  border: 1px solid #e3eaf1;
  border-radius: 12px;
  padding: 18px;
  height: 100%;
  transition: transform 0.2s, box-shadow 0.2s;
}
.req-info-box:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
.req-info-box-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.req-info-icon {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.req-info-icon--blue {
  background: linear-gradient(135deg, #1976d2, #42a5f5);
}
.req-info-icon--amber {
  background: linear-gradient(135deg, #f57c00, #ffb74d);
}
.req-info-title {
  font-weight: 600;
  font-size: 0.95rem;
  color: #37474f;
}
.req-feature-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.req-feature-list li {
  position: relative;
  padding-left: 22px;
  margin-bottom: 8px;
  line-height: 1.55;
  color: #546e7a;
  font-size: 0.9rem;
}
.req-feature-list li::before {
  content: '✔';
  position: absolute;
  left: 0;
  top: 0;
  color: #0097a7;
  font-weight: bold;
  font-size: 0.85rem;
}

/* ─── Data Table Card ─── */
.req-table-card {
  border-radius: 16px !important;
  overflow: hidden;
}
.req-table-header {
  background: linear-gradient(135deg, #00405e 0%, #006494 100%);
  padding: 14px 20px;
}
.req-table-header-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}
.req-table-header-title {
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
}

/* ─── Data Table Styling ─── */
.req-data-table {
  border-radius: 0 !important;
}
.req-data-table >>> thead th {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%) !important;
  color: #37474f !important;
  font-weight: 700 !important;
  font-size: 0.82rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #cfd8dc !important;
  padding: 14px 16px !important;
}
.req-data-table >>> tbody tr {
  transition: background 0.2s;
}
.req-data-table >>> tbody tr:hover {
  background: linear-gradient(135deg, #e0f7fa 0%, #f1f8ff 100%) !important;
}
.req-data-table >>> tbody td {
  padding: 12px 16px !important;
  font-size: 0.9rem;
  color: #455a64;
  border-bottom: 1px solid #eceff1;
  max-width: 200px !important;
  overflow: hidden;
  text-overflow: ellipsis;
}
.req-data-table >>> .v-data-footer {
  border-top: 2px solid #e0e0e0;
  padding: 8px 16px;
}

/* ─── Buttons ─── */
.req-btn-back {
  background: linear-gradient(135deg, #78909c, #90a4ae) !important;
  color: #fff !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  padding: 8px 24px !important;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 8px rgba(120, 144, 156, 0.3) !important;
  transition: all 0.25s !important;
}
.req-btn-back:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 14px rgba(120, 144, 156, 0.45) !important;
}
.req-btn-proceed {
  background: linear-gradient(135deg, #00405e 0%, #0097a7 100%) !important;
  color: #fff !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  padding: 8px 28px !important;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 8px rgba(0, 151, 167, 0.3) !important;
  transition: all 0.25s !important;
}
.req-btn-proceed:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 14px rgba(0, 151, 167, 0.45) !important;
}

/* ─── Dialogs ─── */
.req-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}
.req-dialog-header {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%);
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.req-dialog-header--amber {
  background: linear-gradient(135deg, #e65100 0%, #f57c00 50%, #ffb74d 100%);
}
.req-dialog-header-icon {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}
.req-dialog-header-icon--amber {
  background: rgba(255, 255, 255, 0.25);
}

/* ─── Review Fields ─── */
.req-review-field {
  background: #f8fafc;
  border: 1px solid #e3eaf1;
  border-radius: 10px;
  padding: 14px 16px;
  transition: all 0.2s;
}
.req-review-field:hover {
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.req-review-field-label {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #90a4ae;
  margin-bottom: 6px;
}
.req-review-field-value {
  font-size: 0.95rem;
  color: #37474f;
  font-weight: 500;
  padding-left: 2px;
}

/* ─── Preview Dialog (keep gradient header) ─── */
.requirement-preview-card {
  border-radius: 16px !important;
  overflow: hidden;
}
.dialog-header {
  position: absolute;
  right: 0;
  z-index: 1;
  padding: 8px;
}
.headline-section {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%);
  width: 100%;
  margin: 0;
  padding: 16px !important;
}

/* ─── Misc ─── */
.gap-1 {
  gap: 4px;
}
.truncate-text {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.req-justification-input >>> .v-input__control .v-input__slot {
  border-radius: 10px !important;
}
</style>
